import { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Palette, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/lib/theme/ThemeProvider";
import ThemeEditor from "@/components/appearance/ThemeEditor";
import AppearanceActions from "@/components/appearance/AppearanceActions";
import { describeTheme } from "@/components/appearance/describe";

function ClinicDraftNote({ onBack, onStop }) {
  return (
    <div className="space-y-3 rounded-lg border border-le-border bg-le-subtle p-4 text-sm text-le-t-body" data-testid="appearance-clinic-draft-note">
      <p>You&apos;re previewing a clinic theme draft. Only you can see it until you publish it.</p>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={onBack}>Back to clinic settings</Button>
        <Button size="sm" variant="ghost" onClick={onStop}>Stop preview</Button>
      </div>
    </div>
  );
}

// Non-modal side panel: the real page stays visible and usable behind it.
export default function AppearancePanel() {
  const th = useTheme();
  const navigate = useNavigate();
  const closeRef = useRef(null);
  const { eff, panelOpen, setPanelOpen } = th;

  useEffect(() => {
    if (!panelOpen) return undefined;
    closeRef.current?.focus();
    const el = document.documentElement;
    el.dataset.appearancePanel = "open";
    const onKey = (e) => { if (e.key === "Escape") setPanelOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); delete el.dataset.appearancePanel; };
  }, [panelOpen, setPanelOpen]);

  if (!panelOpen || !eff) return null;
  const d = describeTheme(eff, th.presets, th.settings);
  const clinicDraft = th.draft?.kind === "clinic";

  return (
    <aside
      role="dialog"
      aria-modal="false"
      aria-labelledby="appearance-panel-title"
      className="fixed inset-y-0 right-0 z-[60] flex w-full flex-col border-l border-le-border bg-le-surface shadow-2xl sm:w-[400px] animate-in slide-in-from-right duration-200"
      data-testid="appearance-panel"
    >
      <div className="flex items-start justify-between gap-3 border-b border-le-border px-5 py-4">
        <div className="min-w-0">
          <h2 id="appearance-panel-title" className="flex items-center gap-2 font-display text-lg font-bold text-le-text">
            <Palette className="h-4 w-4 text-le-primary-text" aria-hidden /> Appearance
          </h2>
          <p className="mt-0.5 text-xs text-le-t-2" data-testid="appearance-panel-source">
            {d.name} · {d.source}
          </p>
        </div>
        <Button ref={closeRef} variant="ghost" size="icon" onClick={() => setPanelOpen(false)} aria-label="Close panel (your preview stays on)" data-testid="appearance-panel-close">
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="thin-scroll flex-1 space-y-5 overflow-y-auto px-5 py-5">
        {clinicDraft ? (
          <ClinicDraftNote onBack={() => { setPanelOpen(false); navigate("/settings?tab=appearance"); }} onStop={th.cancelPreview} />
        ) : (
          <ThemeEditor compact settings={th.settings} onChange={th.updateDraft} eff={eff} presets={th.presets} issues={th.preview?.issues || []} />
        )}
        <p className="text-xs text-le-t-2">Changes preview on this page straight away. Nothing is saved until you choose Save.</p>
        <Link to="/appearance" onClick={() => setPanelOpen(false)} className="inline-block text-sm font-medium text-le-primary-text underline-offset-4 hover:underline" data-testid="appearance-open-settings">
          Open full appearance settings →
        </Link>
      </div>
      <AppearanceActions stacked />
    </aside>
  );
}
